import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';

/**
 * Skillsets.
 *
 * Twelve rows that three other modules lean on: the calendar colours every
 * staff chip by skillset, clients are charged per skillset, and the hire rate
 * here is the cost side of the Jobs margin. Changing a colour here repaints the
 * whole calendar, which is why the screen shows the usage counts next to each
 * one rather than making that discoverable only by breaking something.
 *
 * Laid out like the other module screens — PageHeader, search and Add above a
 * paginated Paper table — so it does not read as a different app to Staff
 * Groups or Clients.
 */

interface Skillset {
    readonly id: number;
    readonly title: string;
    readonly colour: string | null;
    readonly textColour: string | null;
    readonly hireRate: number | null;
    readonly isActive: boolean;
    readonly staffCount: number;
    readonly clientRateCount: number;
}

/** The colours the legacy calendar used, so existing skillsets keep their look. */
const PALETTE = [
    '#098BFF', '#3AFF0A', '#FF7702', '#F7FF02', '#FF779C',
    '#3C0AFF', '#FF26A5', '#FF0707', '#860FFF', '#04F7FF',
    '#F81AFF', '#BCFF08', '#FFFFFF',
] as const;

export function Skillsets(): React.JSX.Element {
    const theme = useTheme();
    const [skillsets, setSkillsets] = useState<readonly Skillset[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);
    const [editing, setEditing] = useState<Skillset | 'new' | null>(null);
    const [employeeColourOpen, setEmployeeColourOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch<{ skillsets: readonly Skillset[] }>('/skillsets-admin')
            .then((r) => {
                if (!cancelled) {
                    setSkillsets(r.skillsets);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load skillsets');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    function remove(s: Skillset): void {
        apiFetch<unknown>(`/skillsets-admin/${String(s.id)}`, { method: 'DELETE' })
            .then(reload)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not remove');
            });
    }

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return skillsets.filter((s) => needle === '' || s.title.toLowerCase().includes(needle));
    }, [skillsets, search]);
    const visible = filtered.slice(page * pageSize, (page + 1) * pageSize);

    const activeCount = skillsets.filter((s) => s.isActive).length;
    const missingRate = skillsets.filter((s) => s.hireRate === null && s.isActive).length;

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader
                title="Skillsets"
                subtitle="Sets each person's colour on the calendar, and what clients are charged by"
            />

            {error !== null ? (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => {
                        setError(null);
                    }}
                >
                    {error}
                </Alert>
            ) : null}

            <Box
                sx={{
                    mb: 3,
                    display: 'flex',
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                }}
            >
                <TextField
                    placeholder="Search skillsets..."
                    size="small"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    slotProps={{
                        input: { startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> },
                    }}
                    sx={{ flex: 1, maxWidth: { xs: '100%', sm: 300 } }}
                />
                <Stack direction="row" gap={1.5} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        startIcon={<PaletteOutlinedIcon />}
                        onClick={() => {
                            setEmployeeColourOpen(true);
                        }}
                    >
                        Employee Colour View
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing('new'); }}>
                        Add Skillset
                    </Button>
                </Stack>
            </Box>

            {loading && skillsets.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : null}

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 600, '& th, & td': { whiteSpace: 'nowrap' } }}>
                    <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: 72 }}>Colour</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Skillset</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                Hire rate
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Used by</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    {loading
                                        ? 'Loading...'
                                        : search !== ''
                                          ? 'No skillsets match your search.'
                                          : 'No skillsets yet. Click "Add Skillset" to create one.'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            visible.map((s) => {
                                const inUse = s.staffCount > 0 || s.clientRateCount > 0;
                                return (
                                    <TableRow key={s.id} hover sx={{ opacity: s.isActive ? 1 : 0.5 }}>
                                        <TableCell>
                                            {/* Shown as the chip actually draws on the calendar,
                                                text colour included — a swatch alone hides an
                                                unreadable combination. */}
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 20,
                                                    borderRadius: 0.75,
                                                    bgcolor: s.colour ?? 'transparent',
                                                    border: s.colour === null ? '1px dashed' : 'none',
                                                    borderColor: 'divider',
                                                    color: s.textColour ?? '#000',
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                Abc
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {s.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {s.hireRate === null ? (
                                                <Typography variant="body2" color="text.disabled">
                                                    —
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2">${s.hireRate.toFixed(2)}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color={inUse ? 'text.primary' : 'text.disabled'}
                                            >
                                                {inUse
                                                    ? [
                                                          s.staffCount > 0
                                                              ? `${String(s.staffCount)} staff`
                                                              : null,
                                                          s.clientRateCount > 0
                                                              ? `${String(s.clientRateCount)} rates`
                                                              : null,
                                                      ]
                                                          .filter((v): v is string => v !== null)
                                                          .join(' · ')
                                                    : 'Nothing'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                size="small"
                                                checked={s.isActive}
                                                onChange={(e) => {
                                                    apiFetch<unknown>(`/skillsets-admin/${String(s.id)}`, {
                                                        method: 'PATCH',
                                                        body: JSON.stringify({ isActive: e.target.checked }),
                                                    })
                                                        .then(reload)
                                                        .catch((err: unknown) => {
                                                            setError(
                                                                err instanceof Error
                                                                    ? err.message
                                                                    : 'Could not save',
                                                            );
                                                        });
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setEditing(s);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                    title={
                                                        inUse
                                                            ? 'In use — turn it off instead of deleting it'
                                                            : 'Delete'
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={inUse}
                                                            sx={{ color: inUse ? undefined : 'error.main' }}
                                                            onClick={() => {
                                                                remove(s);
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filtered.length}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(_event, next) => {
                    setPage(next);
                }}
                onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            {/* Eleven of twelve have no hire rate in the legacy data, which is why
                most client rates show no margin. Worth saying once, here. */}
            {missingRate > 0 ? (
                <Alert severity="info" sx={{ mt: 3, fontSize: '0.78rem' }}>
                    {missingRate} of {activeCount} active skillsets have no hire rate. Client rates show no
                    margin without one.
                </Alert>
            ) : null}

            <SkillsetDialog
                open={editing !== null}
                skillset={editing === 'new' ? null : editing}
                onClose={() => {
                    setEditing(null);
                }}
                onSaved={() => {
                    setEditing(null);
                    reload();
                }}
            />

            <EmployeeColourDialog
                open={employeeColourOpen}
                onClose={() => {
                    setEmployeeColourOpen(false);
                }}
            />
        </Container>
    );
}

// ─── Dialog ─────────────────────────────────────────────────────────────────

interface DialogProps {
    readonly open: boolean;
    readonly skillset: Skillset | null;
    readonly onClose: () => void;
    readonly onSaved: () => void;
}

function SkillsetDialog({ open, skillset, onClose, onSaved }: DialogProps): React.JSX.Element {
    const [title, setTitle] = useState('');
    const [colour, setColour] = useState<string>(PALETTE[0]);
    const [textColour, setTextColour] = useState('#000000');
    const [hireRate, setHireRate] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        setError(null);
        setTitle(skillset?.title ?? '');
        setColour(skillset?.colour ?? PALETTE[0]);
        setTextColour(skillset?.textColour ?? '#000000');
        setHireRate(skillset?.hireRate === null || skillset === null ? '' : String(skillset.hireRate));
    }, [open, skillset]);

    function save(): void {
        setSaving(true);
        setError(null);
        const body = {
            title: title.trim(),
            colour,
            textColour,
            hireRate: hireRate.trim() === '' ? null : Number(hireRate),
        };
        const path = skillset === null ? '/skillsets-admin' : `/skillsets-admin/${String(skillset.id)}`;
        apiFetch<unknown>(path, { method: skillset === null ? 'POST' : 'PATCH', body: JSON.stringify(body) })
            .then(onSaved)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const usageWarning =
        skillset !== null && (skillset.staffCount > 0 || skillset.clientRateCount > 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{skillset === null ? 'Add New Skillset' : 'Edit Skillset'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Name"
                        size="small"
                        required
                        autoFocus
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                        }}
                    />

                    <Box>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75 }}>
                            Calendar colour
                        </Typography>
                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                            {PALETTE.map((c) => (
                                <Box
                                    key={c}
                                    onClick={() => {
                                        setColour(c);
                                    }}
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        bgcolor: c,
                                        cursor: 'pointer',
                                        border: '2px solid',
                                        borderColor: colour === c ? 'common.white' : 'transparent',
                                        '&:hover': { transform: 'scale(1.12)' },
                                        transition: 'transform 100ms',
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Stack direction="row" gap={1.5} alignItems="center">
                        <Button
                            size="small"
                            variant={textColour === '#000000' ? 'contained' : 'outlined'}
                            onClick={() => {
                                setTextColour('#000000');
                            }}
                        >
                            Dark text
                        </Button>
                        <Button
                            size="small"
                            variant={textColour === '#FFFFFF' ? 'contained' : 'outlined'}
                            onClick={() => {
                                setTextColour('#FFFFFF');
                            }}
                        >
                            Light text
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        {/* The chip as it will draw on the board. */}
                        <Box
                            sx={{
                                px: 1,
                                height: 22,
                                borderRadius: 0.75,
                                bgcolor: colour,
                                color: textColour,
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {title.trim() === '' ? 'Preview' : title}
                        </Box>
                    </Stack>

                    <TextField
                        label="Hire rate"
                        size="small"
                        value={hireRate}
                        onChange={(e) => {
                            setHireRate(e.target.value);
                        }}
                        slotProps={{ input: { startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> } }}
                        helperText="Cost per hour. Used for the margin on client rates and job profit."
                    />

                    {usageWarning ? (
                        <Alert severity="warning" sx={{ fontSize: '0.78rem' }}>
                            Used by{' '}
                            {[
                                skillset.staffCount > 0 ? `${String(skillset.staffCount)} staff` : null,
                                skillset.clientRateCount > 0
                                    ? `${String(skillset.clientRateCount)} client rates`
                                    : null,
                            ]
                                .filter((v): v is string => v !== null)
                                .join(' and ')}
                            . A colour change repaints their chips across the whole calendar.
                        </Alert>
                    ) : null}

                    {error !== null ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={save} disabled={saving || title.trim() === ''}>
                    {skillset === null ? 'Create' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Employee colour dialog ─────────────────────────────────────────────────

interface EmployeeColour {
    readonly colour: string | null;
    readonly textColour: string | null;
}

interface EmployeeColourDialogProps {
    readonly open: boolean;
    readonly onClose: () => void;
}

/**
 * The one colour Employee-group staff see for everyone on the employee app
 * calendar. Admin and Super Admin calendars keep colouring by skillset.
 */
function EmployeeColourDialog({ open, onClose }: EmployeeColourDialogProps): React.JSX.Element {
    const [saved, setSaved] = useState<EmployeeColour | null>(null);
    const [colour, setColour] = useState<string>(PALETTE[0]);
    const [textColour, setTextColour] = useState('#FFFFFF');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        setError(null);
        apiFetch<EmployeeColour>('/skillsets-admin/employee-colour')
            .then((r) => {
                if (!cancelled) {
                    setSaved(r);
                    setColour(r.colour ?? PALETTE[0]);
                    setTextColour(r.textColour ?? '#FFFFFF');
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load the employee colour');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    function save(next: EmployeeColour): void {
        setSaving(true);
        setError(null);
        apiFetch<EmployeeColour>('/skillsets-admin/employee-colour', {
            method: 'PUT',
            body: JSON.stringify(next),
        })
            .then(onClose)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Employee Colour View</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
                        This will be the only colour of employees on the employee calendar view. Staff in the
                        Employee group see everyone in this colour; the Admin and Super Admin calendars still
                        colour each person by skillset.
                    </Alert>

                    <Box>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75 }}>
                            Employee calendar colour
                        </Typography>
                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                            {PALETTE.map((c) => (
                                <Box
                                    key={c}
                                    onClick={() => {
                                        setColour(c);
                                    }}
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        bgcolor: c,
                                        cursor: 'pointer',
                                        border: '2px solid',
                                        borderColor: colour === c ? 'common.white' : 'transparent',
                                        '&:hover': { transform: 'scale(1.12)' },
                                        transition: 'transform 100ms',
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Stack direction="row" gap={1.5} alignItems="center">
                        <Button
                            size="small"
                            variant={textColour === '#000000' ? 'contained' : 'outlined'}
                            onClick={() => {
                                setTextColour('#000000');
                            }}
                        >
                            Dark text
                        </Button>
                        <Button
                            size="small"
                            variant={textColour === '#FFFFFF' ? 'contained' : 'outlined'}
                            onClick={() => {
                                setTextColour('#FFFFFF');
                            }}
                        >
                            Light text
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Box
                            sx={{
                                px: 1,
                                height: 22,
                                borderRadius: 0.75,
                                bgcolor: colour,
                                color: textColour,
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            Employee
                        </Box>
                    </Stack>

                    {saved !== null && saved.colour === null ? (
                        <Typography variant="caption" color="text.secondary">
                            Not set yet — the employee calendar currently uses skillset colours.
                        </Typography>
                    ) : null}

                    {error !== null ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                {saved !== null && saved.colour !== null ? (
                    <Button
                        color="inherit"
                        disabled={saving}
                        onClick={() => {
                            save({ colour: null, textColour: null });
                        }}
                        sx={{ mr: 'auto' }}
                    >
                        Use skillset colours
                    </Button>
                ) : null}
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={saving}
                    onClick={() => {
                        save({ colour, textColour });
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
